import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import type { Achievement } from '../lib/api.js';

export function Achievements() {
  const [items, setItems] = useState<Achievement[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .achievements()
      .then((rows) => {
        if (cancelled) return;
        setItems(rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'не удалось загрузить');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const unlockedCount = items?.filter((i) => i.unlocked).length ?? 0;
  const total = items?.length ?? 0;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <h2 className="heading-display text-xl text-gold">Достижения</h2>
        {items && (
          <span className="text-[10px] uppercase tracking-wider text-ivory/40">
            {unlockedCount} / {total}
          </span>
        )}
      </div>

      {error && (
        <p
          className="rounded-2xl px-3 py-2 text-xs text-coral"
          style={{
            background: 'var(--color-glass)',
            border: '1px solid color-mix(in oklab, var(--color-coral) 35%, transparent)',
          }}
        >
          {error}
        </p>
      )}
      {!error && items === null && <p className="text-xs text-ivory/55">Загружаем…</p>}

      {items && (
        <ul className="flex flex-col gap-1.5">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-3 rounded-2xl px-3 py-2.5"
              style={{
                background: 'var(--color-glass)',
                border: a.unlocked
                  ? '1px solid color-mix(in oklab, var(--color-gold) 55%, transparent)'
                  : '1px solid color-mix(in oklab, var(--color-gold) 18%, transparent)',
                opacity: a.unlocked ? 1 : 0.7,
              }}
            >
              <span
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full"
                style={{
                  background: a.unlocked ? 'var(--color-gold)' : 'var(--color-glass)',
                  color: a.unlocked ? 'var(--color-felt-deep)' : 'var(--color-ivory)',
                  border: a.unlocked
                    ? 'none'
                    : '1px solid color-mix(in oklab, var(--color-ivory) 22%, transparent)',
                }}
              >
                {a.unlocked ? <Check /> : <Lock />}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 14 }}>{a.title}</span>
                <span className="text-[11px] text-ivory/55">{a.caption}</span>
                {!a.unlocked && a.target > 1 && (
                  <div
                    className="mt-1 h-1 rounded-full overflow-hidden"
                    style={{ background: 'color-mix(in oklab, var(--color-ivory) 14%, transparent)' }}
                  >
                    <div
                      className="h-full"
                      style={{
                        width: `${Math.min(100, (a.current / a.target) * 100)}%`,
                        background: 'var(--color-gold)',
                      }}
                    />
                  </div>
                )}
              </div>
              {!a.unlocked && (
                <span className="shrink-0 text-[11px] text-ivory/55" style={{ fontFamily: 'var(--font-display)' }}>
                  {a.current}/{a.target}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Check() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="5 12 10 17 19 7" />
    </svg>
  );
}

function Lock() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}
